import Swiper from 'swiper';
import { FreeMode, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import { RECT_WIPE_CLOSED, RECT_WIPE_OPEN, OVERLAY_WIPE_OPEN_SECONDS, OVERLAY_WIPE_REDUCED_SECONDS, OVERLAY_WIPE_CLOSE_RATE } from './motion-tokens.js';

const showDialog = (dialog) => {
    if (!dialog || dialog.open) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
};

const closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
};

export const normalizeProjectGalleryUrl = (value = '') => {
    if (!value) return '';

    try {
        const url = new URL(value, window.location.href);
        const cleanPath = decodeURIComponent(url.pathname)
            .replace(/-\d+x\d+(?=\.[a-z0-9]+$)/i, '')
            .replace(/-scaled(?=\.[a-z0-9]+$)/i, '');
        return `${url.origin}${cleanPath}`.toLowerCase();
    } catch (error) {
        return String(value)
            .split(/[?#]/)[0]
            .replace(/-\d+x\d+(?=\.[a-z0-9]+$)/i, '')
            .replace(/-scaled(?=\.[a-z0-9]+$)/i, '')
            .toLowerCase();
    }
};

const createWipeDialogController = ({ dialog, gsap, lenis, reducedMotion = false }) => {
    const shell = dialog?.querySelector('[data-project-modal-wipe]');
    if (!dialog || !shell) return null;

    const revealItems = [...shell.querySelectorAll('[data-project-modal-reveal]')];
    const duration = reducedMotion ? OVERLAY_WIPE_REDUCED_SECONDS : OVERLAY_WIPE_OPEN_SECONDS;
    let requestedOpen = false;

    gsap.set(shell, { clipPath: RECT_WIPE_CLOSED });
    if (revealItems.length) gsap.set(revealItems, { autoAlpha: 0, y: reducedMotion ? 0 : 18 });

    const resumePageScroll = () => {
        document.body.classList.remove('project-modal-is-open');
        if (!document.body.classList.contains('menu-is-open') && !document.body.classList.contains('search-is-open') && !document.body.classList.contains('city-explorer-is-active')) {
            lenis?.start?.();
        }
    };

    const timeline = gsap.timeline({
        paused: true,
        defaults: { overwrite: 'auto' },
        onComplete: () => {
            if (requestedOpen) dialog.dispatchEvent(new CustomEvent('projectmodal:opened'));
        },
        onReverseComplete: () => {
            if (requestedOpen) return;
            closeDialog(dialog);
            resumePageScroll();
            dialog.dispatchEvent(new CustomEvent('projectmodal:closed'));
        },
    });

    timeline.to(shell, {
        clipPath: RECT_WIPE_OPEN,
        duration,
        ease: reducedMotion ? 'none' : 'power4.inOut',
    });

    if (revealItems.length) {
        timeline.to(
            revealItems,
            {
                autoAlpha: 1,
                y: 0,
                duration: reducedMotion ? 0.1 : 0.48,
                stagger: reducedMotion ? 0 : 0.035,
                ease: 'power3.out',
            },
            reducedMotion ? 0.03 : 0.24,
        );
    }

    const open = () => {
        requestedOpen = true;
        showDialog(dialog);
        dialog.dispatchEvent(new CustomEvent('projectmodal:opening', { bubbles: true }));
        document.body.classList.add('project-modal-is-open');
        lenis?.stop?.();
        window.requestAnimationFrame(() => timeline.timeScale(1).play());
    };

    const close = () => {
        if (!dialog.open) return;
        requestedOpen = false;
        if (timeline.progress() <= 0.001) {
            closeDialog(dialog);
            resumePageScroll();
            return;
        }
        timeline.timeScale(reducedMotion ? 2.5 : OVERLAY_WIPE_CLOSE_RATE).reverse();
    };

    dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
    });

    return { open, close, timeline };
};

const initHeroMotion = ({ root, gsap, ScrollTrigger, reducedMotion }) => {
    const hero = root.querySelector('[data-project-hero]');
    if (!hero || !gsap) return null;

    const image = hero.querySelector('[data-project-hero-image]');
    const overlay = hero.querySelector('[data-project-hero-overlay]');
    const copy = hero.querySelector('[data-project-hero-copy]');
    const revealItems = [...hero.querySelectorAll('[data-project-hero-reveal]')];
    const indicator = hero.querySelector('[data-project-scroll-indicator]');
    const runner = hero.querySelector('[data-project-scroll-indicator-runner]');
    const track = runner?.parentElement || null;

    if (reducedMotion) {
        gsap.set([image, ...revealItems, indicator].filter(Boolean), { clearProps: 'all' });
        return null;
    }

    const context = gsap.context(() => {
        const intro = gsap.timeline({ defaults: { overwrite: 'auto' } });

        // Keep the initial project load calm: a simple fade only, no load-time zoom.
        if (image) {
            intro.fromTo(
                image,
                { autoAlpha: 0 },
                { autoAlpha: 1, duration: 1.15, ease: 'power2.out' },
                0,
            );
        }

        if (revealItems.length) {
            intro.fromTo(
                revealItems,
                { autoAlpha: 0 },
                { autoAlpha: 1, duration: 0.88, stagger: 0.08, ease: 'power2.out' },
                0.18,
            );
        }

        if (indicator) {
            intro.fromTo(indicator, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.8, ease: 'power2.out' }, 0.62);
        }

        // Parallax/zoom belongs to scroll, not to the initial page load.
        if (image && ScrollTrigger) {
            gsap.fromTo(
                image,
                { scale: 1, yPercent: 0 },
                {
                    scale: 1.08,
                    yPercent: 4.5,
                    ease: 'none',
                    immediateRender: false,
                    scrollTrigger: {
                        trigger: hero,
                        start: 'top top',
                        end: 'bottom top',
                        scrub: 1.15,
                    },
                },
            );
        }

        if (overlay && ScrollTrigger) {
            gsap.to(overlay, {
                opacity: 0.9,
                ease: 'none',
                scrollTrigger: {
                    trigger: hero,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 1,
                },
            });
        }

        // Do not move hero text while scrolling; a restrained fade keeps it readable.
        if (copy && ScrollTrigger) {
            gsap.to(copy, {
                opacity: 0.84,
                ease: 'none',
                scrollTrigger: {
                    trigger: hero,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 0.8,
                },
            });
        }

        // Continuous "there is more" cue: the short segment travels inside the fixed line.
        if (runner && track) {
            const travel = () => Math.max(0, track.clientHeight - runner.offsetHeight);
            const indicatorLoop = gsap.timeline({ repeat: -1, repeatDelay: 0.08, repeatRefresh: true });
            indicatorLoop
                .set(runner, { y: 0, autoAlpha: 0, scaleY: 0.45, transformOrigin: '50% 50%' })
                .to(runner, { autoAlpha: 1, scaleY: 1, duration: 0.16, ease: 'power2.out' })
                .to(runner, { y: travel, scaleY: 0.72, duration: 1.08, ease: 'power2.inOut' }, '<')
                .to(runner, { autoAlpha: 0, scaleY: 0.35, duration: 0.18, ease: 'power2.out' }, '-=0.14');
        }
    }, hero);

    return context;
};

const initProjectScrollStory = ({ root, gsap, ScrollTrigger, reducedMotion }) => {
    if (!gsap || !ScrollTrigger || reducedMotion) return null;

    const story = root.querySelector('[data-project-story]');
    const contextual = [...root.querySelectorAll('[data-project-scroll-reveal]')];
    const storyBlocks = story ? [...story.children] : [];
    const mediaSelector = 'figure, .wp-block-gallery, .wp-block-cover, .wp-block-media-text, .wp-block-columns';

    const context = gsap.context(() => {
        contextual.forEach((node) => {
            const target = node.matches('[data-project-related]') ? node.firstElementChild : node;
            if (!target) return;

            gsap.fromTo(
                target,
                { opacity: 0.38 },
                {
                    opacity: 1,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: node,
                        start: 'top 92%',
                        end: 'top 74%',
                        scrub: 0.45,
                    },
                },
            );
        });

        storyBlocks.forEach((block) => {
            const isMedia = block.matches(mediaSelector) || Boolean(block.querySelector?.(mediaSelector));

            // ScrollTrigger only changes emphasis. No text translation or scale is used,
            // so reading position stays visually stable while content becomes fully opaque.
            gsap.fromTo(
                block,
                { opacity: isMedia ? 0.5 : 0.32 },
                {
                    opacity: 1,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: block,
                        start: 'top 94%',
                        end: 'top 72%',
                        scrub: isMedia ? 0.58 : 0.46,
                    },
                },
            );
        });
    }, root);

    return context;
};

const initProjectRelatedMotion = ({ root, gsap, ScrollTrigger, reducedMotion }) => {
    const section = root.querySelector('[data-project-related]');
    if (!section || !gsap || !ScrollTrigger || reducedMotion) return null;

    const header = section.querySelector('[data-project-related-header]');
    const cards = [...section.querySelectorAll('[data-project-related-card]')];

    return gsap.context(() => {
        if (header) {
            const heading = header.querySelector('h2');
            const link = header.querySelector('a');
            const targets = [heading, link].filter(Boolean);
            if (targets.length) {
                gsap.fromTo(
                    targets,
                    { autoAlpha: 0, y: 24 },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.92,
                        stagger: 0.1,
                        ease: 'power3.out',
                        scrollTrigger: { trigger: header, start: 'top 88%', once: true },
                    },
                );
            }
        }

        if (cards.length) {
            gsap.fromTo(
                cards,
                { autoAlpha: 0.35, y: 28 },
                {
                    autoAlpha: 1,
                    y: 0,
                    duration: 1.05,
                    stagger: 0.08,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: section.querySelector('[data-project-related-slider]') || section, start: 'top 86%', once: true },
                },
            );
        }
    }, section);
};

const initProjectDock = ({ root, gsap, ScrollTrigger, reducedMotion }) => {
    const dock = root.querySelector('[data-project-dock]');
    const hero = root.querySelector('[data-project-hero]');
    const related = root.querySelector('[data-project-related]');
    const article = root.querySelector('article');
    if (!dock || !hero || !ScrollTrigger) return null;

    const show = () => {
        dock.style.pointerEvents = 'auto';
        if (!gsap || reducedMotion) {
            dock.style.opacity = '1';
            dock.style.transform = 'none';
            return;
        }
        gsap.to(dock, { autoAlpha: 1, y: 0, duration: 0.42, ease: 'power3.out', overwrite: true });
    };

    const hide = () => {
        dock.style.pointerEvents = 'none';
        if (!gsap || reducedMotion) {
            dock.style.opacity = '0';
            return;
        }
        gsap.to(dock, { autoAlpha: 0, y: 16, duration: 0.3, ease: 'power2.out', overwrite: true });
    };

    if (gsap && !reducedMotion) gsap.set(dock, { autoAlpha: 0, y: 16 });
    else hide();

    const trigger = ScrollTrigger.create({
        trigger: hero,
        start: 'top -18%',
        endTrigger: related || article || root,
        end: related ? 'top bottom' : 'bottom bottom',
        onEnter: show,
        onEnterBack: show,
        onLeave: hide,
        onLeaveBack: hide,
        onRefresh: (self) => {
            if (self.isActive) show();
            else hide();
        },
    });

    return { revert: () => { trigger.kill(); hide(); } };
};

const initProjectAccordions = ({ root, gsap, ScrollTrigger, lenis, reducedMotion }) => {
    const items = [...root.querySelectorAll('[data-project-accordion-item]')];
    if (!items.length) return;

    const refreshLayout = () => {
        lenis?.resize?.();
        ScrollTrigger?.refresh?.();
    };

    const setImmediate = (item, open) => {
        const panel = item.querySelector('[data-project-accordion-panel]');
        const icon = item.querySelector('[data-project-accordion-icon]');
        if (open) item.setAttribute('open', '');
        else item.removeAttribute('open');
        if (panel) gsap?.set(panel, { clearProps: 'height,opacity,visibility,transform' });
        if (icon) gsap?.set(icon, { rotate: open ? 45 : 0 });
    };

    const closeItem = (item, { immediate = false } = {}) => {
        if (!item.open) return;
        const panel = item.querySelector('[data-project-accordion-panel]');
        const icon = item.querySelector('[data-project-accordion-icon]');

        if (immediate || reducedMotion || !gsap || !panel) {
            setImmediate(item, false);
            refreshLayout();
            return;
        }

        gsap.killTweensOf([panel, icon].filter(Boolean));
        gsap.set(panel, { height: panel.getBoundingClientRect().height, overflow: 'hidden' });
        gsap.to(panel, {
            height: 0,
            autoAlpha: 0,
            y: -6,
            duration: 0.36,
            ease: 'power3.inOut',
            onComplete: () => {
                item.removeAttribute('open');
                gsap.set(panel, { clearProps: 'height,opacity,visibility,transform,overflow' });
                refreshLayout();
            },
        });
        if (icon) gsap.to(icon, { rotate: 0, duration: 0.34, ease: 'power3.inOut' });
    };

    const openItem = (item) => {
        items.forEach((other) => {
            if (other !== item) closeItem(other);
        });

        const panel = item.querySelector('[data-project-accordion-panel]');
        const icon = item.querySelector('[data-project-accordion-icon]');

        if (reducedMotion || !gsap || !panel) {
            setImmediate(item, true);
            refreshLayout();
            return;
        }

        item.setAttribute('open', '');
        gsap.killTweensOf([panel, icon].filter(Boolean));
        gsap.set(panel, { height: 0, autoAlpha: 0, y: -6, overflow: 'hidden' });
        const targetHeight = panel.scrollHeight;
        gsap.to(panel, {
            height: targetHeight,
            autoAlpha: 1,
            y: 0,
            duration: 0.46,
            ease: 'power3.inOut',
            onComplete: () => {
                gsap.set(panel, { height: 'auto', clearProps: 'opacity,visibility,transform,overflow' });
                refreshLayout();
            },
        });
        if (icon) gsap.to(icon, { rotate: 45, duration: 0.42, ease: 'power3.inOut' });
    };

    items.forEach((item) => {
        const trigger = item.querySelector('[data-project-accordion-trigger]');
        const panel = item.querySelector('[data-project-accordion-panel]');
        const icon = item.querySelector('[data-project-accordion-icon]');
        if (!trigger || !panel) return;

        if (item.open) {
            gsap?.set(panel, { height: 'auto', autoAlpha: 1 });
            if (icon) gsap?.set(icon, { rotate: 45 });
        }

        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            if (item.open) closeItem(item);
            else openItem(item);
        });
    });
};

export function initSingleProject({ lenis = null, gsap = null, ScrollTrigger = null, reducedMotion = false } = {}) {
    const root = document.querySelector('[data-single-project]');
    if (!root) return;

    const motionContexts = [
        initHeroMotion({ root, gsap, ScrollTrigger, reducedMotion }),
        initProjectScrollStory({ root, gsap, ScrollTrigger, reducedMotion }),
        initProjectRelatedMotion({ root, gsap, ScrollTrigger, reducedMotion }),
        initProjectDock({ root, gsap, ScrollTrigger, reducedMotion }),
    ].filter(Boolean);

    root.querySelectorAll('[data-project-scroll]').forEach((button) => {
        button.addEventListener('click', () => {
            const target = document.querySelector(button.dataset.projectScroll || '');
            if (!target) return;
            if (lenis?.scrollTo) lenis.scrollTo(target, { offset: -48, duration: 1.35 });
            else target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    });

    initProjectAccordions({ root, gsap, ScrollTrigger, lenis, reducedMotion });

    const feedback = root.querySelector('[data-project-tool-feedback]');
    let feedbackTimer = 0;
    const setFeedback = (message) => {
        if (!feedback) return;
        feedback.textContent = message;
        clearTimeout(feedbackTimer);
        feedbackTimer = window.setTimeout(() => {
            feedback.textContent = '';
        }, 2200);
    };

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setFeedback(root.dataset.projectLinkCopied || 'Link copied');
        } catch (error) {
            const field = document.createElement('textarea');
            field.value = window.location.href;
            field.style.position = 'fixed';
            field.style.opacity = '0';
            document.body.appendChild(field);
            field.select();
            document.execCommand('copy');
            field.remove();
            setFeedback(root.dataset.projectLinkCopied || 'Link copied');
        }
    };

    root.querySelectorAll('[data-project-copy]').forEach((button) => button.addEventListener('click', copyUrl));
    root.querySelectorAll('[data-project-share]').forEach((button) => {
        button.addEventListener('click', async () => {
            if (navigator.share) {
                try {
                    await navigator.share({ title: document.title, url: window.location.href });
                } catch (error) {
                    // Native share dismissal needs no feedback.
                }
            } else {
                await copyUrl();
            }
        });
    });
    // Print behavior intentionally stays native until the dedicated print view is supplied.
    root.querySelectorAll('[data-project-print]').forEach((button) => button.addEventListener('click', () => window.print()));

    const modal3d = root.querySelector('[data-project-3d-modal]');
    const controller3d = gsap ? createWipeDialogController({ dialog: modal3d, gsap, lenis, reducedMotion }) : null;
    let viewer3d = null;
    let viewer3dPromise = null;

    const ensure3dViewer = async () => {
        if (!modal3d) return null;
        if (viewer3d) return viewer3d;

        viewer3dPromise ||= import('./project-3d-viewer.js');
        const { createProject3DViewer } = await viewer3dPromise;
        viewer3d = createProject3DViewer(modal3d, {
            modelUrl: modal3d.dataset.modelUrl || '',
        });
        viewer3d?.load?.();
        return viewer3d;
    };

    const activate3dViewer = async () => {
        try {
            const viewer = await ensure3dViewer();
            if (!modal3d?.open) return;
            viewer?.start?.();
            window.requestAnimationFrame(() => viewer?.resize?.());
        } catch (error) {
            const loading = modal3d?.querySelector('[data-project-3d-loading]');
            const errorBox = modal3d?.querySelector('[data-project-3d-error]');
            const errorText = modal3d?.querySelector('[data-project-3d-error-text]');
            if (loading) loading.hidden = true;
            if (errorBox) errorBox.hidden = false;
            if (errorText) errorText.textContent = 'The 3D viewer could not be initialized.';
        }
    };

    const open3d = () => {
        if (!modal3d) return;
        if (controller3d) controller3d.open();
        else {
            showDialog(modal3d);
            lenis?.stop?.();
        }
        void activate3dViewer();
    };
    const close3d = () => {
        viewer3d?.stop?.();
        if (controller3d) controller3d.close();
        else {
            closeDialog(modal3d);
            lenis?.start?.();
        }
    };
    root.querySelectorAll('[data-project-3d-open]').forEach((button) => button.addEventListener('click', open3d));
    modal3d?.querySelector('[data-project-3d-close]')?.addEventListener('click', close3d);
    modal3d?.addEventListener('projectmodal:opened', () => void activate3dViewer());
    modal3d?.addEventListener('projectmodal:closed', () => viewer3d?.stop?.());

    const galleryDataNode = root.querySelector('[data-project-gallery-data]');
    let galleryItems = [];

    if (galleryDataNode) {
        try {
            const parsed = JSON.parse(galleryDataNode.textContent || '[]');
            if (Array.isArray(parsed)) {
                galleryItems = parsed.filter((item) => item && typeof item.url === 'string' && item.url.length > 0);
            }
        } catch (error) {
            galleryItems = [];
        }
    }

    // Gutenberg can render responsive/resized URLs that are not always present
    // in the server payload. Merge every narrative image into the gallery before
    // building lookup maps so every image in the project story is guaranteed to
    // open the lightbox. Existing payload items keep their authored order.
    const storyImages = [...root.querySelectorAll('[data-project-story] img')];
    const knownIds = new Set(galleryItems.map((item) => Number(item.id || 0)).filter((id) => id > 0));
    const knownUrls = new Set(galleryItems.map((item) => normalizeProjectGalleryUrl(item.url)).filter(Boolean));

    storyImages.forEach((image) => {
        const classMatch = String(image.className || '').match(/(?:^|\s)wp-image-(\d+)(?:\s|$)/);
        const attachmentId = classMatch ? Number(classMatch[1]) : 0;
        const linkedUrl = image.closest('a[href]')?.getAttribute('href') || '';
        const sourceUrl = linkedUrl
            || image.getAttribute('data-orig-file')
            || image.getAttribute('data-large-file')
            || image.currentSrc
            || image.src
            || image.getAttribute('src')
            || '';
        const normalizedUrl = normalizeProjectGalleryUrl(sourceUrl);

        if ((attachmentId > 0 && knownIds.has(attachmentId)) || (normalizedUrl && knownUrls.has(normalizedUrl))) return;
        if (!sourceUrl) return;

        const figure = image.closest('figure');
        const caption = figure?.querySelector('figcaption')?.textContent?.trim() || '';
        galleryItems.push({
            id: attachmentId,
            url: sourceUrl,
            alt: image.getAttribute('alt') || '',
            caption,
        });
        if (attachmentId > 0) knownIds.add(attachmentId);
        if (normalizedUrl) knownUrls.add(normalizedUrl);
    });

    const galleryLaunchers = [...root.querySelectorAll('[data-project-gallery-launch]')];
    const lightbox = root.querySelector('[data-project-lightbox]');
    const lightboxStage = lightbox?.querySelector('[data-project-lightbox-stage]');
    const lightboxImage = lightbox?.querySelector('[data-project-lightbox-image]');
    const lightboxCaption = lightbox?.querySelector('[data-project-lightbox-caption]');
    const lightboxCounter = lightbox?.querySelector('[data-project-lightbox-counter]');
    const lightboxPrev = lightbox?.querySelector('[data-project-lightbox-prev]');
    const lightboxNext = lightbox?.querySelector('[data-project-lightbox-next]');
    const lightboxController = gsap ? createWipeDialogController({ dialog: lightbox, gsap, lenis, reducedMotion }) : null;
    let activeIndex = 0;
    let imageRequest = 0;

    const preloadAdjacent = () => {
        if (galleryItems.length < 2) return;
        [activeIndex - 1, activeIndex + 1].forEach((candidate) => {
            const item = galleryItems[(candidate + galleryItems.length) % galleryItems.length];
            if (!item?.url) return;
            const preload = new Image();
            preload.src = item.url;
        });
    };

    const applyLightboxItem = (item, index) => {
        if (!lightboxImage || !item) return;
        lightboxImage.src = item.url;
        lightboxImage.alt = item.alt || '';
        if (lightboxCaption) {
            lightboxCaption.textContent = item.caption || '';
            lightboxCaption.hidden = !lightboxCaption.textContent;
        }
        if (lightboxCounter) lightboxCounter.textContent = `${index + 1} / ${galleryItems.length}`;
    };

    const updateLightbox = (index, { animate = true, direction = 1 } = {}) => {
        if (!galleryItems.length || !lightboxImage) return;
        const nextIndex = (index + galleryItems.length) % galleryItems.length;
        const item = galleryItems[nextIndex];
        activeIndex = nextIndex;
        const requestId = ++imageRequest;

        if (!animate) {
            applyLightboxItem(item, nextIndex);
            if (gsap) gsap.set(lightboxImage, { autoAlpha: 1, x: 0, xPercent: 0, scale: 1 });
            preloadAdjacent();
            return;
        }

        let committed = false;

        const commit = () => {
            if (committed || requestId !== imageRequest) return;
            committed = true;

            if (reducedMotion || !gsap || !lightboxImage.src) {
                applyLightboxItem(item, nextIndex);
                if (gsap) gsap.set(lightboxImage, { autoAlpha: 1, x: 0, xPercent: 0, scale: 1 });
                preloadAdjacent();
                return;
            }

            gsap.killTweensOf(lightboxImage);
            gsap.to(lightboxImage, {
                autoAlpha: 0,
                xPercent: -4 * direction,
                scale: 0.988,
                duration: 0.28,
                ease: 'power2.in',
                onComplete: () => {
                    if (requestId !== imageRequest) return;
                    applyLightboxItem(item, nextIndex);
                    gsap.fromTo(
                        lightboxImage,
                        { autoAlpha: 0, x: 0, xPercent: 4 * direction, scale: 0.988 },
                        { autoAlpha: 1, x: 0, xPercent: 0, scale: 1, duration: 0.72, ease: 'power3.out' },
                    );
                    preloadAdjacent();
                },
            });
        };

        if (!item?.url) return;
        // Touch navigation must respond immediately. Adjacent images are already
        // opportunistically preloaded, so waiting for a network load here makes
        // mobile swipes feel sticky on slower devices/connections.
        if (window.matchMedia('(pointer: coarse)').matches) {
            commit();
        } else {
            const preload = new Image();
            preload.onload = commit;
            preload.onerror = commit;
            preload.src = item.url;
            if (preload.complete) commit();
        }
    };

    const openLightbox = (index = 0) => {
        if (!galleryItems.length) return;
        updateLightbox(index, { animate: false });
        if (lightboxController) lightboxController.open();
        else {
            showDialog(lightbox);
            lenis?.stop?.();
        }
    };

    const closeLightbox = () => {
        if (lightboxController) lightboxController.close();
        else {
            closeDialog(lightbox);
            lenis?.start?.();
        }
    };

    galleryLaunchers.forEach((button) => button.addEventListener('click', () => openLightbox(0)));

    const galleryIndexesById = new Map();
    const galleryIndexesByUrl = new Map();
    galleryItems.forEach((item, index) => {
        const attachmentId = Number(item.id || 0);
        const normalizedUrl = normalizeProjectGalleryUrl(item.url);
        if (attachmentId > 0) {
            const indexes = galleryIndexesById.get(attachmentId) || [];
            indexes.push(index);
            galleryIndexesById.set(attachmentId, indexes);
        }
        if (normalizedUrl) {
            const indexes = galleryIndexesByUrl.get(normalizedUrl) || [];
            indexes.push(index);
            galleryIndexesByUrl.set(normalizedUrl, indexes);
        }
    });

    const occurrenceById = new Map();
    const occurrenceByUrl = new Map();

    storyImages.forEach((image) => {
        const classMatch = String(image.className || '').match(/(?:^|\s)wp-image-(\d+)(?:\s|$)/);
        const attachmentId = classMatch ? Number(classMatch[1]) : 0;
        let index = -1;

        if (attachmentId > 0 && galleryIndexesById.has(attachmentId)) {
            const indexes = galleryIndexesById.get(attachmentId);
            const occurrence = occurrenceById.get(attachmentId) || 0;
            index = indexes[Math.min(occurrence, indexes.length - 1)] ?? -1;
            occurrenceById.set(attachmentId, occurrence + 1);
        }

        if (index < 0) {
            const candidates = [image.currentSrc, image.src, image.getAttribute('src')].filter(Boolean).map(normalizeProjectGalleryUrl);
            for (const candidate of candidates) {
                const indexes = galleryIndexesByUrl.get(candidate) || [];
                if (!indexes.length) continue;
                const occurrence = occurrenceByUrl.get(candidate) || 0;
                index = indexes[Math.min(occurrence, indexes.length - 1)] ?? -1;
                occurrenceByUrl.set(candidate, occurrence + 1);
                break;
            }
        }

        if (index < 0) return;
        image.dataset.projectGalleryImage = 'true';
        image.dataset.projectGalleryIndex = String(index);
        image.setAttribute('role', 'button');
        image.setAttribute('tabindex', '0');
        image.setAttribute('aria-label', root.dataset.projectGalleryOpenLabel || 'Open image in gallery');

        const activate = (event) => {
            event.preventDefault();
            event.stopPropagation();
            openLightbox(Number(image.dataset.projectGalleryIndex || 0));
        };

        image.addEventListener('click', activate);
        image.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') activate(event);
        });
    });

    if (galleryItems.length <= 1) {
        if (lightboxPrev) lightboxPrev.hidden = true;
        if (lightboxNext) lightboxNext.hidden = true;
    }

    lightbox?.querySelector('[data-project-lightbox-close]')?.addEventListener('click', closeLightbox);
    lightboxPrev?.addEventListener('click', () => updateLightbox(activeIndex - 1, { direction: -1 }));
    lightboxNext?.addEventListener('click', () => updateLightbox(activeIndex + 1, { direction: 1 }));
    lightbox?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') updateLightbox(activeIndex - 1, { direction: -1 });
        if (event.key === 'ArrowRight') updateLightbox(activeIndex + 1, { direction: 1 });
    });
    const settleLightboxDrag = () => {
        if (!lightboxImage || !gsap) return;
        gsap.to(lightboxImage, { x: 0, duration: 0.24, ease: 'power3.out', overwrite: true });
    };

    let swipeState = null;
    const removeSwipeGhost = () => {
        swipeState?.ghost?.remove?.();
        if (swipeState) swipeState.ghost = null;
    };

    const createSwipeGhost = (direction) => {
        if (!swipeState || !lightboxImage || !lightboxStage) return null;
        const nextIndex = (activeIndex + direction + galleryItems.length) % galleryItems.length;
        const item = galleryItems[nextIndex];
        if (!item?.url) return null;
        const stageRect = lightboxStage.getBoundingClientRect();
        const ghost = lightboxImage.cloneNode(false);
        ghost.removeAttribute('data-project-lightbox-image');
        ghost.src = item.url;
        ghost.alt = item.alt || '';
        ghost.setAttribute('aria-hidden', 'true');
        Object.assign(ghost.style, {
            position: 'absolute',
            left: '0px',
            top: '0px',
            width: `${stageRect.width}px`,
            height: `${stageRect.height}px`,
            maxWidth: 'none',
            maxHeight: 'none',
            objectFit: 'contain',
            pointerEvents: 'none',
            willChange: 'transform',
            zIndex: '2',
        });
        lightboxStage.appendChild(ghost);
        swipeState.ghost = ghost;
        swipeState.direction = direction;
        swipeState.nextIndex = nextIndex;
        return ghost;
    };

    lightboxStage?.addEventListener('pointerdown', (event) => {
        if (galleryItems.length <= 1 || (event.pointerType === 'mouse' && event.button !== 0)) return;
        removeSwipeGhost();
        swipeState = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), dx: 0, horizontal: false, ghost: null, direction: 0, nextIndex: activeIndex };
        lightboxStage.setPointerCapture?.(event.pointerId);
        if (gsap && lightboxImage) gsap.killTweensOf(lightboxImage);
    });

    lightboxStage?.addEventListener('pointermove', (event) => {
        if (!swipeState || event.pointerId !== swipeState.id || !lightboxImage) return;
        const dx = event.clientX - swipeState.x;
        const dy = event.clientY - swipeState.y;
        swipeState.dx = dx;
        if (!swipeState.horizontal && Math.abs(dx) > 3 && Math.abs(dx) > Math.abs(dy) * 0.85) swipeState.horizontal = true;
        if (!swipeState.horizontal) return;
        event.preventDefault();

        const direction = dx < 0 ? 1 : -1;
        if (!swipeState.ghost || swipeState.direction !== direction) {
            swipeState.ghost?.remove?.();
            swipeState.ghost = null;
            createSwipeGhost(direction);
        }
        const width = Math.max(1, lightboxStage.clientWidth);
        lightboxImage.style.transform = `translate3d(${dx}px,0,0)`;
        if (swipeState.ghost) swipeState.ghost.style.transform = `translate3d(${dx + (direction * width)}px,0,0)`;
    }, { passive: false });

    const finishSwipe = (event) => {
        if (!swipeState || event.pointerId !== swipeState.id || !lightboxImage) return;
        const state = swipeState;
        lightboxStage?.releasePointerCapture?.(event.pointerId);
        const elapsed = Math.max(16, performance.now() - state.time);
        const velocity = Math.abs(state.dx) / elapsed;
        const threshold = Math.min(30, Math.max(16, window.innerWidth * 0.045));
        const accepted = state.horizontal && (Math.abs(state.dx) >= threshold || velocity > 0.18);

        if (!gsap) {
            lightboxImage.style.transform = '';
            if (accepted) updateLightbox(state.nextIndex, { animate: false });
            state.ghost?.remove?.();
            swipeState = null;
            return;
        }

        if (!accepted || !state.ghost) {
            gsap.to(lightboxImage, { x: 0, duration: 0.22, ease: 'power3.out', overwrite: true, clearProps: 'transform' });
            if (state.ghost) gsap.to(state.ghost, { x: state.direction * lightboxStage.clientWidth, duration: 0.22, ease: 'power3.out', onComplete: () => state.ghost?.remove?.() });
            swipeState = null;
            return;
        }

        const width = Math.max(1, lightboxStage.clientWidth);
        const outgoingX = -state.direction * width;
        gsap.to(lightboxImage, { x: outgoingX, duration: 0.26, ease: 'power2.out', overwrite: true });
        gsap.to(state.ghost, {
            x: 0,
            duration: 0.26,
            ease: 'power2.out',
            overwrite: true,
            onComplete: () => {
                activeIndex = state.nextIndex;
                applyLightboxItem(galleryItems[activeIndex], activeIndex);
                gsap.set(lightboxImage, { x: 0, xPercent: 0, autoAlpha: 1, clearProps: 'transform' });
                state.ghost?.remove?.();
                preloadAdjacent();
            },
        });
        swipeState = null;
    };

    lightboxStage?.addEventListener('pointerup', finishSwipe);
    lightboxStage?.addEventListener('pointercancel', finishSwipe);

    const slider = root.querySelector('[data-project-related-slider]');
    if (slider) {
        new Swiper(slider, {
            modules: [FreeMode, Navigation],
            slidesPerView: 1.1,
            spaceBetween: 16,
            speed: 1180,
            grabCursor: true,
            followFinger: true,
            threshold: 3,
            resistanceRatio: 0.82,
            touchRatio: 0.82,
            shortSwipes: true,
            longSwipes: true,
            longSwipesMs: 180,
            longSwipesRatio: 0.12,
            watchSlidesProgress: true,
            freeMode: { enabled: true, sticky: false, momentum: true, momentumRatio: 0.52, momentumVelocityRatio: 0.58, minimumVelocity: 0.015 },
            navigation: {
                prevEl: slider.querySelector('.project-related__prev'),
                nextEl: slider.querySelector('.project-related__next'),
            },
            breakpoints: {
                640: { slidesPerView: 2.15, spaceBetween: 24 },
                1024: { slidesPerView: 3.15, spaceBetween: 28 },
                1440: { slidesPerView: 3.45, spaceBetween: 32 },
            },
            on: {
                progress(instance, progress) {
                    slider.style.setProperty('--slider-progress', String(Math.max(0, Math.min(1, progress))));
                },
            },
        });
    }

    window.addEventListener('pagehide', () => {
        motionContexts.forEach((context) => context?.revert?.());
    }, { once: true });
}
